import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FiFile, 
  FiDownload, 
  FiEye, 
  FiCheckCircle, 
  FiMapPin, 
  FiClock, 
  FiUser, 
  FiBriefcase,
  FiX
} from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Tooltip as LeafletTooltip } from 'react-leaflet';
import L from 'leaflet';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import 'leaflet/dist/leaflet.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import Api from '../../../Api';
import { PageComponent, Skeletons } from '../../../components';
import './style.css';

// Fix Leaflet icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Utility functions
function formatCurrency(value) {
  if (!value) return '';
  if (String(value).includes('R$')) return value;
  let v = String(value).replace(/\D/g, '');
  v = (v / 100).toFixed(2) + '';
  v = v.replace('.', ',');
  v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  return `R$ ${v}`;
}

function formatPhone(value) {
  if (!value) return '';
  if (String(value).includes('(')) return value;
  let v = String(value).replace(/\D/g, '');
  if (v.length > 11) v = v.slice(0, 11);
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0,2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0,2)}) ${v.slice(2,6)}-${v.slice(6)}`;
  return `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
}

function formatDate(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }
    return date.toLocaleDateString('pt-BR');
  } catch (e) {
    return value;
  }
}

function base64ToBlob(base64, type) {
  try {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1] || type;
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: contentType });
  } catch (e) {
    console.error('Error converting base64 to blob', e);
    return null;
  }
}

export default function InspectionPublicView() {
  const { token } = useParams();
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxData, setLightboxData] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const response = await Api.getPublicInspection(token);
        setExecution(response);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [token]);

  const config = execution?.config;
  const answers = execution?.answers || {};
  const references = execution?.references || {};

  const questionMap = useMemo(() => {
    if (!config || !config.sections) return {};
    const map = {};
    config.sections.forEach(section => {
      section.questions.forEach(q => {
        map[q.id] = q;
      });
    });
    return map;
  }, [config]);

  const companyLogoUrl = useMemo(() => {
    if (!execution?.company?.logo) return null;
    const logo = execution.company.logo;
    if (logo.type === 'Buffer' && logo.data) {
      const base64String = btoa(
        new Uint8Array(logo.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      return `data:image/png;base64,${base64String}`;
    }
    return typeof logo === 'string' ? logo : null;
  }, [execution]);

  if (loading) {
    return (
      <PageComponent authenticatedPage={false}>
        <div className="container">
          <Skeletons.TableSkeleton />
        </div>
      </PageComponent>
    );
  }

  if (!execution) {
    return (
      <PageComponent authenticatedPage={false}>
        <div className="container">
          <div className="public-form-wrapper" style={{ textAlign: 'center' }}>
            <h1>Inspeção não encontrada</h1>
            <p>O link pode estar inválido ou ter sido desativado.</p>
          </div>
        </div>
      </PageComponent>
    );
  }

  function isQuestionVisible(question, visited = new Set()) {
    if (!question.conditional?.enabled) return true;
    const { referenceId, expectedValue } = question.conditional;
    if (!referenceId) return true;
    if (visited.has(question.id)) return true;
    visited.add(question.id);
    const refQuestion = questionMap[referenceId];
    if (refQuestion) {
      if (!isQuestionVisible(refQuestion, new Set(visited))) return false;
    }
    const refAnswer = answers[referenceId];
    if (refAnswer === undefined || refAnswer === null) {
       return expectedValue === 'nao_contem';
    }
    if (expectedValue === 'contem') {
      if (Array.isArray(refAnswer)) return refAnswer.length > 0;
      if (typeof refAnswer === 'string') return refAnswer.trim().length > 0;
      return true;
    }
    if (expectedValue === 'nao_contem') {
      if (Array.isArray(refAnswer)) return refAnswer.length === 0;
      if (typeof refAnswer === 'string') return refAnswer.trim().length === 0;
      return false;
    }
    if (Array.isArray(refAnswer)) return refAnswer.includes(expectedValue);
    return String(refAnswer) === String(expectedValue);
  }

  function handleViewFile(file) {
    const blob = base64ToBlob(file.data || file.content, file.type);
    if (blob) {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  }

  function renderAnswer(question) {
    const value = answers[question.id];
    if (value === undefined || value === null || value === '') {
      return <span className="text-gray-400 italic">Sem resposta</span>;
    }

    switch (question.type) {
      case 'photo':
        if (!Array.isArray(value) || value.length === 0) return <span className="text-gray-400 italic">Sem fotos</span>;
        return (
          <div className="preview-grid">
            {value.map((photo, idx) => (
              <div key={idx} className="preview-item">
                <img src={photo.data || photo.content} alt={photo.name} className="preview-image" onClick={() => setLightboxData({ images: value, index: idx })} />
                <div className="file-actions-overlay">
                  <button type="button" className="file-action-btn" onClick={() => setLightboxData({ images: value, index: idx })} title="Visualizar">
                    <FiEye size={16} />
                  </button>
                  <a href={photo.data || photo.content} download={photo.name} className="file-action-btn" title="Baixar">
                    <FiDownload size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        );
      case 'document':
        if (!Array.isArray(value) || value.length === 0) return <span className="text-gray-400 italic">Sem documentos</span>;
        return (
          <div className="preview-grid">
            {value.map((doc, idx) => (
              <div key={idx} className="preview-item">
                <div className="preview-file">
                  <FiFile size={24} />
                  <span>{doc.name}</span>
                </div>
                <div className="file-actions-overlay">
                  <button type="button" className="file-action-btn" onClick={() => handleViewFile(doc)} title="Visualizar">
                    <FiEye size={16} />
                  </button>
                  <a href={doc.data || doc.content} download={doc.name} className="file-action-btn" title="Baixar">
                    <FiDownload size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        );
      case 'signature':
        return (
          <div className="signature-display">
            <img src={value} alt="Assinatura" className="signature-image" onClick={() => setLightboxData({ images: [{ data: value, name: 'Assinatura' }], index: 0 })} />
          </div>
        );
      case 'monetary': return <span>{formatCurrency(value)}</span>;
      case 'phone': return <span>{formatPhone(value)}</span>;
      case 'date': return <span>{formatDate(value)}</span>;
      case 'single_choice':
      case 'select':
        return (
          <div className="radio-group">
            {question.options?.map((option, idx) => {
              const isSelected = value === option.text || String(value) === String(option.id) || (option.value && String(value) === String(option.value));
              return (
                <div key={idx} className={`custom-radio ${isSelected ? 'selected' : ''}`}>
                  <div className="radio-indicator" />
                  <span>{option.text}</span>
                </div>
              );
            })}
          </div>
        );
      case 'multiple_choice': {
        const rawValues = Array.isArray(value) ? value : [value];
        const rawSet = new Set(rawValues.map(v => String(v)));
        return (
          <div className="checkbox-group">
            {question.options?.map((option, idx) => {
              const isSelected = rawSet.has(String(option.text)) || rawSet.has(String(option.id)) || (option.value && rawSet.has(String(option.value)));
              return (
                <div key={idx} className={`custom-checkbox ${isSelected ? 'selected' : ''}`}>
                  <div className="checkbox-indicator" />
                  <span>{option.text}</span>
                </div>
              );
            })}
          </div>
        );
      }
      case 'employee': return <span>{references.employees?.[value] || value}</span>;
      case 'station':
      case 'service_station': return <span>{references.stations?.[value] || value}</span>;
      case 'vehicle': return <span>{references.vehicles?.[value] || value}</span>;
      case 'machinery': return <span>{references.machineries?.[value] || value}</span>;
      case 'multi_machinery': {
        const ids = Array.isArray(value) ? value : [value];
        return <span>{ids.map(id => references.machineries?.[id] || id).join(', ')}</span>;
      }
      default: return <span>{value}</span>;
    }
  }

  return (
    <PageComponent authenticatedPage={false}>
      <div className="container">
        <div className="public-view-wrapper">
          <div className="public-view-header">
            <div className="company-info">
              <div className="company-logo">
                {companyLogoUrl ? <img src={companyLogoUrl} alt="Logo" /> : <FiBriefcase size={32} />}
              </div>
              <div>
                <h2>{execution.company?.trade_name || execution.company?.corporate_name}</h2>
                <p>Relatório de Inspeção</p>
              </div>
            </div>
            <div className="execution-badge" style={{ backgroundColor: config?.color || '#10B981' }}>
              #{execution.id}
            </div>
          </div>

          <div className="inspection-main-info">
            <h1>{execution.inspection_name}</h1>
            <div className="info-grid">
              <div className="info-item">
                <FiClock size={16} />
                <span><strong>Executado em:</strong> {formatDate(execution.executed_at)}</span>
              </div>
              <div className="info-item">
                <FiUser size={16} />
                <span><strong>Executor:</strong> {execution.executor_name}</span>
              </div>
              {execution.reference && (
                <div className="info-item">
                  <FiMapPin size={16} />
                  <span><strong>Referência:</strong> {execution.reference}</span>
                </div>
              )}
            </div>
          </div>

          {execution.latitude && execution.longitude && (
            <div className="map-section">
              <h3><FiMapPin /> Localização da Execução</h3>
              <div className="map-container-wrapper">
                <MapContainer center={[execution.latitude, execution.longitude]} zoom={15} style={{ height: '300px', width: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[execution.latitude, execution.longitude]}>
                    <LeafletTooltip permanent>Local da Inspeção</LeafletTooltip>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          )}

          <div className="inspection-content">
            {config?.sections?.map((section, sIdx) => (
              <div key={sIdx} className="section-card">
                <div className="section-header">
                  <h3>{section.title}</h3>
                </div>
                <div className="questions-list">
                  {section.questions?.filter(q => isQuestionVisible(q)).map((q, qIdx) => (
                    <div key={qIdx} className="question-row">
                      <div className="question-label">{q.label}</div>
                      <div className="question-answer">{renderAnswer(q)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {execution.observations && (
            <div className="observations-section">
              <h3>Observações Gerais</h3>
              <p>{execution.observations}</p>
            </div>
          )}

          <div className="public-view-footer">
            <p>Este é um documento digital gerado automaticamente pelo sistema Clockfy.</p>
          </div>
        </div>
      </div>

      {lightboxData && (
        <div className="lightbox-overlay" onClick={() => setLightboxData(null)}>
          <button className="lightbox-close" onClick={() => setLightboxData(null)}><FiX size={32} /></button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <Swiper modules={[Navigation, Pagination, Zoom]} navigation pagination={{ clickable: true }} zoom initialSlide={lightboxData.index} style={{ width: '100%', height: '100%' }}>
              {lightboxData.images.map((img, i) => (
                <SwiperSlide key={i}>
                  <div className="swiper-zoom-container">
                    <img src={img.data || img.content} alt={img.name} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}
    </PageComponent>
  );
}
